'use client';

import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface ChartDataPoint {
  date: string;
  price: number;
}

interface PriceChartProps {
  fishName: string;
  data: ChartDataPoint[];
  onClose: () => void;
}

const PriceChart = ({ fishName, data, onClose }: PriceChartProps) => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  const timeRanges = [
    { value: '7d' as const, label: '7 Days' },
    { value: '30d' as const, label: '30 Days' },
    { value: '90d' as const, label: '90 Days' },
  ];

  const getFilteredData = () => {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    return data.slice(-days);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-md shadow-elevation-md p-3">
          <p className="text-sm font-medium text-foreground mb-1">{payload[0].payload.date}</p>
          <p className="font-data text-lg font-bold text-primary">
            ₹{payload[0].value.toFixed(2)}/kg
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-5xl mx-4 bg-card border border-border rounded-lg shadow-elevation-xl animate-slide-down">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="font-heading font-bold text-2xl text-foreground mb-1">
              {fishName} Price Trend
            </h2>
            <p className="text-sm text-muted-foreground">Historical price data and market trends</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close chart"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-2">
              {timeRanges.map((range) => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`px-4 py-2 rounded-md font-medium text-sm transition-smooth ${
                    timeRange === range.value
                      ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                      : 'bg-muted text-foreground hover:bg-border'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>

            <button className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-md font-medium text-sm transition-smooth hover:bg-border">
              <Icon name="ArrowDownTrayIcon" size={18} />
              <span>Export Data</span>
            </button>
          </div>

          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={getFilteredData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  stroke="rgba(0,0,0,0.5)"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="rgba(0,0,0,0.5)"
                  label={{ value: 'Price (₹/kg)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#C41E3A"
                  strokeWidth={3}
                  dot={{ fill: '#C41E3A', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Price (₹/kg)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-muted rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ArrowTrendingUpIcon" size={20} className="text-success" />
                <span className="text-sm font-medium text-muted-foreground">Highest Price</span>
              </div>
              <p className="font-data text-2xl font-bold text-foreground">
                ₹{Math.max(...getFilteredData().map(d => d.price)).toFixed(2)}
              </p>
            </div>

            <div className="bg-muted rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ArrowTrendingDownIcon" size={20} className="text-error" />
                <span className="text-sm font-medium text-muted-foreground">Lowest Price</span>
              </div>
              <p className="font-data text-2xl font-bold text-foreground">
                ₹{Math.min(...getFilteredData().map(d => d.price)).toFixed(2)}
              </p>
            </div>

            <div className="bg-muted rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ChartBarIcon" size={20} className="text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Average Price</span>
              </div>
              <p className="font-data text-2xl font-bold text-foreground">
                ₹{(getFilteredData().reduce((sum, d) => sum + d.price, 0) / getFilteredData().length).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceChart;